import { Suspense, lazy } from "react";
import { Navigate, useRoutes, useLocation } from "react-router-dom";

import MainLayout from "../layouts/main";
import AuthLayout from "../layouts/auth/auth";

import LoadingScreen from "../components/LoadingScreen/LoadingScreen";
import { useSelector } from "react-redux";

// Dynamic loading component wrapper
const Loadable = (Component) => (props) => {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Component {...props} />
    </Suspense>
  );
};

// Role-based route injection
const getRoleBasedRoutes = (role) => {
  const routes = [];
  if (role === "OPERATOR") {
    routes.push({ path: "account-management", element: <AccountManagement /> });
  }
  return routes;
};

export default function Router() {
  const { role } = useSelector((state) => state.auth);
  const location = useLocation();
  return useRoutes([
    {
      path: "/mongolia",
      element: <AuthLayout />,
      children: [
        { path: "user/login", element: <LoginPage /> },
        { path: "user/new-password", element: <NewPasswordPage /> },
        { path: "user/reset-password", element: <ResetPasswordPage /> },
        { path: "user/new-password/:token", element: <NewPasswordPage /> },
      ],
    },
    {
      path: "/mongolia",
      element: <MainLayout />,
      children: [
        { path: "", element: <Navigate to="/mongolia/home" replace /> },
        { path: "home", element: <Home /> },
        { path: "detection", element: <Detection /> },
        { path: "detail/:id", element: <Detail /> },
        { path: "detailReport/:id", element: <DetailReport /> },
        { path: "detail-tracking/:id", element: <DetailTracking /> },
        { path: "violation", element: <Violation /> },
        { path: "violation-mobile", element: <ViolationMobile /> },
        { path: "nonviolation", element: <NonViolation /> },
        { path: "resource-center", element: <ResourceCenter /> },
        { path: "nonviolation-mobile", element: <NonViolationMobile /> },
        { path: "detection-mobile", element: <DetectionMobile /> },
        { path: "report-mobile", element: <ReportMobile /> },
        { path: "submit", element: <Submit /> },
        { path: "submit-mobile", element: <SubmitMobile /> },
        { path: "report", element: <Report /> },
        ...getRoleBasedRoutes(role),
        { path: "404", element: <Page404 /> },
        { path: "*", element: <Navigate to="/404" replace /> },
      ],
    },
    { path: "*", element: <Navigate to="/404" replace /> },
  ]);
}


const ResourceCenter = Loadable(
  lazy(() => import("../pages/main/ResourceCenter/ResourceCenter"))
);

const LoginPage = Loadable(lazy(() => import("../pages/auth/LoginPage")));
const NewPasswordPage = Loadable(
  lazy(() => import("../pages/auth/NewPasswordPage"))
);
const ResetPasswordPage = Loadable(
  lazy(() => import("../pages/auth/ResetPasswordPage"))
);

const Home = Loadable(lazy(() => import("../pages/main/Home/Home")));

const Detection = Loadable(
  lazy(() => import("../pages/main/Detection/Detection"))
);

const Detail = Loadable(lazy(() => import("../pages/main/Detail/Detail")));
const DetailReport = Loadable(lazy(() => import("../pages/main/DetailReport/DetailReport")));
const DetailTracking = Loadable(lazy(() => import("../pages/main/DetailTracking/DetailTracking")));

const Report = Loadable(lazy(() => import("../pages/main/Report/Report")));

const AccountManagement = Loadable(
  lazy(() => import("../pages/main/AccountManagement/AccountManagement"))
);

const Page404 = Loadable(lazy(() => import("../pages/Page404/Page404")));

const DetectionMobile = Loadable(
  lazy(() => import("../pages/main/DetectionMobile/DetectionMobile"))
);
const ReportMobile = Loadable(
  lazy(() => import("../pages/main/ReportMobile/ReportMobile"))
);
const Violation = Loadable(
  lazy(() => import("../pages/main/Violation/Violation"))
);
const ViolationMobile = Loadable(
  lazy(() => import("../pages/main/ViolationMobile/ViolationMobile"))
);

const NonViolation = Loadable(
  lazy(() => import("../pages/main/NonViolation/NonViolation"))
);
const NonViolationMobile = Loadable(
  lazy(() => import("../pages/main/NonViolationMobile/NonViolationMobile"))
);

const Submit = Loadable(
  lazy(() => import("../pages/main/Submit/Submit"))
);

const SubmitMobile = Loadable(
  lazy(() => import("../pages/main/SubmitMobile/SubmitMobile"))
);

